/*
 * Reading the backend's refusal to create a player whose name is already taken.
 * The uniqueness rule itself lives on the server; the client only reads which
 * record the name collided with, so the form can explain the refusal rather
 * than echoing it.
 */
import { readErrorExtension } from './graphql-error.helpers';
import { getPlayerName } from '../utils/player-name.service';

export interface IPlayerNameConflict {
  // Absent when the colliding record has no date of birth on file — there is
  // then no date for the refusal to name as what tells the two apart.
  dateOfBirth?: string;
}

// The colliding player as the mutation's error describes them, or null for any
// other failure — which the caller surfaces as a general error instead.
export const readPlayerNameConflict = (error: unknown): IPlayerNameConflict | null =>
  readErrorExtension<IPlayerNameConflict>(error, 'playerNameConflict');

export interface IImportFailureGroups {
  nameTakenDated: { name: string; dateOfBirth: string }[];
  nameTakenUndated: { name: string }[];
  invalid: { name: string; details: string[] }[];
  unknown: { name: string }[];
}

// Failed import rows grouped by what the operator has to do about them: a name
// collision needs a date of birth or a nickname, an invalid row needs the field
// it names, an unknown one can only be retried.
export const groupImportFailures = (
  rows: { player?: IPlayer }[],
  results: ICreatePlayerAssessmentRowResult[],
): IImportFailureGroups => {
  const groups: IImportFailureGroups = {
    nameTakenDated: [], nameTakenUndated: [], invalid: [], unknown: [],
  };

  (results || []).forEach((result, index) => {
    const failures = result?.failures;

    if (!failures?.length) return;

    // getPlayerName pads its output when the athlete is not removed.
    const name = getPlayerName({ player: rows[index]?.player, firstNameFirst: true }).trim();
    const nameTaken = failures.find((failure) => failure.reason === 'NAME_TAKEN');

    if (nameTaken) {
      if (nameTaken.detail) groups.nameTakenDated.push({ name, dateOfBirth: nameTaken.detail });
      else groups.nameTakenUndated.push({ name });

      return;
    }

    const details = failures
      .filter((failure) => failure.reason === 'INVALID_FIELD' && failure.detail)
      .map((failure) => failure.detail as string);

    if (details.length) groups.invalid.push({ name, details });
    else groups.unknown.push({ name });
  });

  return groups;
};

export interface IImportOutcomeSummary {
  created: number;
  merged: number;
  skipped: number;
  duplicateRows: number;
  withheld: number;
  refused: number;
  notImported: number;
  contactsCreated: number;
  contactsMerged: number;
}

// What the import actually did, counted per submitted row. The server gives every
// row exactly one outcome, so these sum to the rows submitted plus withheldRowCount.
export const summarizeImportOutcomes = (
  results: ICreatePlayerAssessmentRowResult[],
  withheldRowCount = 0,
): IImportOutcomeSummary => {
  const summary: IImportOutcomeSummary = {
    created: 0, merged: 0, skipped: 0, duplicateRows: 0,
    withheld: withheldRowCount, refused: 0, notImported: withheldRowCount,
    contactsCreated: 0, contactsMerged: 0,
  };

  (results || []).forEach((result) => {
    summary.contactsCreated += result?.contactsCreated || 0;
    summary.contactsMerged += result?.contactsMerged || 0;

    switch (result?.outcome) {
      case 'CREATED': summary.created += 1; break;
      case 'MERGED': summary.merged += 1; break;
      case 'SKIPPED': summary.skipped += 1; break;
      case 'DUPLICATE_ROW': summary.duplicateRows += 1; break;
      // A row the server refused, and the pre-outcome shape where only failures said so.
      default: if (result?.outcome === 'FAILED' || result?.failures?.length) {
        summary.refused += 1;
        summary.notImported += 1;
      }
    }
  });

  return summary;
};
